import { Phone, Mail, Globe, MapPin, Clock } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { SITE, waLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/icons";
import { track } from "@/lib/analytics";

const CONTACT_ROWS = [
  { Icon: Phone, label: "Phone", value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-phone", event: "phone_click" },
  { Icon: WhatsAppIcon, label: "WhatsApp", value: SITE.whatsappDisplay, href: waLink(), testid: "contact-whatsapp", event: "whatsapp_click", external: true },
  { Icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
  { Icon: Globe, label: "Website", value: SITE.domain, href: SITE.url, testid: "contact-website", external: true },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact NRI Property Services Hyderabad | Free Property Assessment"
        description="Let's take care of your Hyderabad property. Contact NRI Property Services — phone, WhatsApp or enquiry form. A Quality Expertz Group Company."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        titleLines={["Let's Take Care of", "Your Hyderabad Property."]}
        copy="Tell us about your property and what you need handled. A property manager will respond — usually the same day."
      />

      <section className="bg-ivory py-24 sm:py-32" data-testid="contact-section">
        <div className="container-site grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div>
            <Reveal>
              <div className="rounded-3xl bg-navy-900 p-8 sm:p-10 lg:sticky lg:top-32">
                <h2 className="font-display text-xl font-extrabold tracking-tight text-white">NRI Property Services</h2>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-400">
                  A Quality Expertz Group Company
                </p>
                <ul className="mt-8 space-y-5">
                  {CONTACT_ROWS.map(({ Icon, label, value, href, testid, event, external }) => (
                    <li key={label}>
                      <a
                        href={href}
                        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        onClick={event ? () => track(event, { location: "contact_page" }) : undefined}
                        className="group flex items-center gap-4"
                        data-testid={testid}
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] transition-colors group-hover:border-gold-500/50">
                          <Icon className="h-4 w-4 text-gold-400" />
                        </span>
                        <span>
                          <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">{label}</span>
                          <span className="block font-display text-sm font-bold text-white transition-colors group-hover:text-gold-400">{value}</span>
                        </span>
                      </a>
                    </li>
                  ))}
                  <li className="flex items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                      <MapPin className="h-4 w-4 text-gold-400" />
                    </span>
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Location</span>
                      <span className="block font-display text-sm font-bold text-white">{SITE.location}</span>
                    </span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                      <Clock className="h-4 w-4 text-gold-400" />
                    </span>
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Service Area</span>
                      <span className="block font-display text-sm font-bold text-white">{SITE.serviceArea}</span>
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <LeadForm source="contact_page" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
